// ============================================================================
// TaxonomyCombobox — input com autocomplete + dedupe normalizado
// Usado em Grupo/Subcategoria/Natureza/SUD (Nível 1/2/3), no lançamento
// individual e no lote. Comparação ignora acento/maiúscula/espaço extra antes
// de decidir se o valor já existe — "Alimentação Natural" e "alimentacao
// natural" contam como o mesmo valor (achado real, 17/09/2026: sem isso, a
// mesma categoria era digitada de formas diferentes ao longo do tempo).
// ============================================================================
import React, { useState, useEffect, useRef } from "react";

export function normalizeTaxonomyValue(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export function TaxonomyCombobox({
  value,
  onChange,
  onCreate,
  options = [],
  placeholder = "",
  className = "",
  disabled = false,
}) {
  const [inputValue, setInputValue] = useState(value || "");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const normalizedInput = normalizeTaxonomyValue(inputValue);
  const filtered = normalizedInput
    ? options.filter((o) => normalizeTaxonomyValue(o).includes(normalizedInput))
    : options;
  const exactMatch = options.find(
    (o) => normalizeTaxonomyValue(o) === normalizedInput,
  );

  const selectValue = (v) => {
    setInputValue(v);
    onChange(v);
    setOpen(false);
  };

  const handleCreate = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (exactMatch) {
      selectValue(exactMatch);
      return;
    }
    if (typeof onCreate === "function") await onCreate(trimmed);
    selectValue(trimmed);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <input
        type="text"
        className={className}
        value={inputValue}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          setInputValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg text-[11px]">
          {filtered.map((o) => (
            <button
              key={o}
              type="button"
              className="block w-full text-left px-3 py-1.5 hover:bg-gray-100 text-gray-700"
              onMouseDown={() => selectValue(o)}
            >
              {o}
            </button>
          ))}
          {filtered.length === 0 && !inputValue.trim() && (
            <div className="px-3 py-1.5 text-gray-400">Nenhuma opção ainda</div>
          )}
          {inputValue.trim() && !exactMatch && (
            <button
              type="button"
              className="block w-full text-left px-3 py-1.5 text-[#C5A059] font-bold hover:bg-[#C5A059]/10 border-t border-gray-100"
              onMouseDown={handleCreate}
            >
              + Criar "{inputValue.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
